import { useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { Badge, Card, EmptyState, Select } from "@/components/ui";
import { formatDateTime } from "@/lib/utils/format";
import type { AdminSnapshot } from "@/lib/types/api";
import { IconCamera } from "./components/icons";

export function SnapshotGalleryPage() {
  const [page, setPage] = useState(1);
  const [eventId, setEventId] = useState("");
  const snapshots = useAsync(
    () => adminApi.snapshots.list({ page, per_page: 24, event_id: eventId ? Number(eventId) : undefined }),
    [page, eventId],
  );
  const items = snapshots.data?.snapshots ?? [];
  const pagination = snapshots.data?.pagination;
  const eventOptions = Array.from(new Map(items.map((item) => [item.event_id, item.event_name])).entries());

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label-eyebrow text-accent/80">Photo booth</span>
          <h1 className="text-xl font-semibold">Photo booth gallery</h1>
          <p className="mt-1 text-sm text-haze-300">Final photos saved by guests from Snapshot sessions.</p>
        </div>
        <Select value={eventId} onChange={(event) => { setEventId(event.target.value); setPage(1); }} className="min-w-[210px]">
          <option value="">All booth events</option>
          {eventOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Select>
      </header>

      {snapshots.error && <Card className="p-4 text-sm text-red-300">Could not load booth photos: {snapshots.error.message}</Card>}
      {snapshots.loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">{Array.from({ length: 12 }).map((_, i) => <div key={i} className="skeleton aspect-[3/4]" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={<IconCamera size={28} />} title="No booth photos yet" description="Completed Snapshot photos will appear here after guests finish a session." />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-6">
          {items.map((item) => <SnapshotTile key={item.id} item={item} />)}
        </div>
      )}

      {pagination && pagination.total_pages > 1 && <nav className="flex items-center justify-center gap-3 text-sm text-haze-400">
        <button className="rounded-lg border border-ink-600 px-3 py-2 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
        <span>Page {pagination.current_page} of {pagination.total_pages}</span>
        <button className="rounded-lg border border-ink-600 px-3 py-2 disabled:opacity-40" disabled={page >= pagination.total_pages} onClick={() => setPage((value) => value + 1)}>Next</button>
      </nav>}
    </div>
  );
}

function SnapshotTile({ item }: { item: AdminSnapshot }) {
  return <a href={item.image_url ?? item.share_url ?? "#"} target="_blank" rel="noreferrer" className="group overflow-hidden rounded-xl border border-ink-600 bg-ink-800 transition hover:border-accent/60">
    <div className="aspect-[3/4] bg-ink-750">{item.image_url ? <img src={item.image_url} alt={`Photo booth capture from ${item.event_name}`} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.02]" /> : <div className="grid h-full place-items-center text-xs text-haze-500">Image unavailable</div>}</div>
    <div className="flex flex-col gap-1 p-3"><div className="flex items-center justify-between gap-2"><span className="truncate text-xs font-semibold text-haze-100">{item.event_name}</span><Badge tone={item.status === "ready" ? "aqua" : "neutral"}>{item.status}</Badge></div><span className="text-2xs text-haze-400">{formatDateTime(item.created_at)}{item.filter_slug ? ` · ${item.filter_slug}` : ""}</span></div>
  </a>;
}
